import { FuelButton, FuelDivider, FuelFilter } from '@frete.com/fuel-react';
import { FuelBottomSheet, FuelDrawer, FuelFlex } from '@frete.com/fuel-station-react';
import { TruckerAway } from 'constants/enums/trucker';
import { useAmplitude } from 'hooks/useAmplitude';
import { AmplitudeData } from 'hooks/useAmplitude/types';
import { useAppContext } from 'hooks/useAppContext';
import { useLoadTruckers } from 'hooks/useLoadTruckers';
import { useTracking } from 'hooks/useTracking';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { dsTheme } from 'constants/styled.contants';
import DDD from './DDD';
import Distance from './Distance';
import Sort from './Sort';
import { TSortList, VALUES } from './Sort/types';
import Vehicles from './Vehicles';
import * as Styled from './styled';

const FiltersDrawer: FC = () => {
  const { fetchTruckers } = useLoadTruckers('lazy');
  const { sendAmplitude } = useAmplitude();
  const { sendTracking } = useTracking();
  const { freightId } = useParams();

  const {
    state: {
      truckers: { radius, sort, vehicles, areaCodes, meta },
      deviceSize: { isMobile },
    },
    dispatch,
  } = useAppContext();
  const windowHeight = useRef(window.innerHeight);

  const [opened, setOpened] = useState<boolean>(false);
  const [radiusValue, setRadiusValue] = useState<number>(TruckerAway.INITIAL_VALUE);
  const [sortChecked, setSortChecked] = useState<string>(sort);
  const [vehiclesChecked, setVehiclesChecked] = useState<number[]>(vehicles);
  const [areaCodesChecked, setAreaCodesChecked] = useState<number[]>(areaCodes);
  const [isDisabledButtonFilter, setIsDisabledButtonFilter] = useState<boolean>(true);
  const [isDisabledButtonClean, setIsDisabledButtonClean] = useState<boolean>(true);

  const isFilterApplied = (arrFilter: string[] | number[], arrContext: string[] | number[]) =>
    arrFilter.length > 0 ||
    (!!arrFilter.length && arrFilter.toString() !== arrContext?.toString()) ||
    arrFilter.length !== arrContext.length;

  const isRadiusApplied = radiusValue !== radius;
  const isSortApplied = sortChecked !== null && sortChecked !== sort;
  const isVehiclesApplied = isFilterApplied(vehiclesChecked, vehicles);
  const isAreaCodeApplied = isFilterApplied(areaCodesChecked, areaCodes);

  useEffect(() => {
    setIsDisabledButtonClean(
      radiusValue === TruckerAway.INITIAL_VALUE &&
        sortChecked === null &&
        vehiclesChecked.length === 0 &&
        areaCodesChecked.length === 0
    );
    setIsDisabledButtonFilter(!isRadiusApplied && !isSortApplied && !isVehiclesApplied && !isAreaCodeApplied);
  }, [isRadiusApplied, isSortApplied, isVehiclesApplied, isAreaCodeApplied]);

  useEffect(() => {
    setRadiusValue(radius);
  }, [radius]);

  const getVehiclesCheckedNames = () => {
    const vehiclesCheckedNames: string[] = [];
    const arrVehicles = meta.freight?.vehicleTypes
      .map((category) => category.types)
      .reduce((result, item) => result.concat(item));
    vehiclesChecked.forEach((vehicle) => {
      vehiclesCheckedNames.push(arrVehicles.find((item) => item.id === vehicle).name);
    });

    return vehiclesCheckedNames.sort();
  };

  const cleanFilters = () => {
    setRadiusValue(TruckerAway.INITIAL_VALUE);
    setSortChecked(null);
    setVehiclesChecked([]);
    setAreaCodesChecked([]);
    dispatch({ type: 'set_is_filter_applied', payload: false });

    if (radius !== TruckerAway.INITIAL_VALUE) {
      dispatch({ type: 'truckers_radius', payload: { radius: TruckerAway.INITIAL_VALUE } });
      fetchTruckers(TruckerAway.INITIAL_VALUE);
    }

    if (sort !== null) {
      dispatch({ type: 'truckers_sort_set', payload: { sort: null } });
    }

    if (areaCodes.length > 0) {
      dispatch({ type: 'truckers_areaCodes_set', payload: [] });
    }

    if (vehicles.length > 0) {
      dispatch({ type: 'truckers_vehicles_clear' });
    }
    setOpened(!opened);
  };

  const ApplyFilters = () => {
    const eventsFiltersApplied: string[] = [];
    let filtersApplied: Partial<AmplitudeData> = {};

    let trackingApplied = {};

    if (isRadiusApplied) {
      dispatch({ type: 'truckers_radius', payload: { radius: radiusValue } });
      fetchTruckers(radiusValue);
      filtersApplied = { radius: radiusValue === 5 ? radiusValue : radiusValue - 5 };
      trackingApplied = { radius: radiusValue === 5 ? radiusValue : radiusValue - 5 };
      eventsFiltersApplied.push('raio');
    }

    if (isSortApplied) {
      dispatch({ type: 'truckers_sort_set', payload: { sort: sortChecked } });
      filtersApplied = {
        ...filtersApplied,
        order: VALUES.find((val: TSortList) => val.value === sortChecked).amplitudePropertyName,
      };
      trackingApplied = {
        ...filtersApplied,
        order: VALUES.find((val: TSortList) => val.value === sortChecked).amplitudePropertyName,
      };
      eventsFiltersApplied.push('ordenar');
    }

    if (isVehiclesApplied) {
      dispatch({ type: 'truckers_vehicles_set', payload: vehiclesChecked });
      filtersApplied = { ...filtersApplied, vehicle: getVehiclesCheckedNames() };
      trackingApplied = { ...filtersApplied, vehicle: getVehiclesCheckedNames() };
      eventsFiltersApplied.push('veiculos');
    }

    if (isAreaCodeApplied) {
      dispatch({ type: 'truckers_areaCodes_set', payload: areaCodesChecked });
      filtersApplied = { ...filtersApplied, ddd: areaCodesChecked };
      trackingApplied = { ...filtersApplied, ddd: areaCodesChecked };
      eventsFiltersApplied.push('filtro-ddd');
    }

    if (isRadiusApplied || isSortApplied || isVehiclesApplied || isAreaCodeApplied) {
      sendAmplitude('trucker_listing_filters_applied', {
        type: eventsFiltersApplied,
        ...filtersApplied,
        freightCode: freightId,
      });
      sendTracking({
        url: window.location.href,
        referrer: document.referrer,
        event: {
          name: 'trucker_listing_filters_applied',
          type: eventsFiltersApplied,
        },
        data: {
          ...trackingApplied,
          freightId: `hashed|${freightId}`,
        },
      });
    }
    dispatch({ type: 'set_is_filter_applied', payload: isAreaCodeApplied || isVehiclesApplied });

    setOpened(!opened);
  };

  const handleOpenFilters = () => {
    setOpened(true);
    sendAmplitude('trucker_listing_filters_opened');
    sendTracking({
      url: window.location.href,
      referrer: document.referrer,
      event: {
        name: 'trucker_listing_filters_opened',
      },
      data: {
        date: new Date(),
      },
    });
  };

  const renderFiltersContent = () => (
    <Styled.FiltersWrapper>
      <Sort checked={sortChecked} setChecked={setSortChecked} />
      <DDD checked={areaCodesChecked} setChecked={setAreaCodesChecked} />
      <FuelFlex
        defaultProps={{
          flexDirection: 'column',
          customMargin: `${dsTheme.spacing.km8} 0 0 0`,
        }}
      >
        <FuelDivider />
      </FuelFlex>
      <Distance sliderValue={radiusValue} setSliderValue={setRadiusValue} />
      <FuelFlex
        defaultProps={{
          flexDirection: 'column',
          customMargin: `${dsTheme.spacing.km8} 0 0 0`,
        }}
      >
        <FuelDivider />
      </FuelFlex>
      <Vehicles checked={vehiclesChecked} setChecked={setVehiclesChecked} />
    </Styled.FiltersWrapper>
  );

  return (
    <>
      <FuelFlex defaultProps={{ justifyContent: 'flex-end' }}>
        <FuelFilter onClick={handleOpenFilters} showIcon>
          Filtros
        </FuelFilter>
      </FuelFlex>

      {!isMobile ? (
        <FuelDrawer
          portalName="portal-trucker-listing"
          isOpen={opened}
          title="Filtros e ordenação"
          onClose={() => setOpened(false)}
          width="md"
          footer={
            <>
              <FuelButton
                onClick={cleanFilters}
                variant="tertiary"
                disabled={isDisabledButtonClean}
                size="large"
                data-testid="clean-filters"
              >
                Limpar filtros
              </FuelButton>
              <FuelButton onClick={ApplyFilters} disabled={isDisabledButtonFilter} size="large">
                Aplicar Filtros
              </FuelButton>
            </>
          }
        >
          {renderFiltersContent()}
        </FuelDrawer>
      ) : (
        <FuelBottomSheet
          portalName="portal-trucker-listing"
          isOpen={opened}
          title="Filtros e ordenação"
          onClose={() => setOpened(false)}
          onConfirm={ApplyFilters}
          onRefuse={cleanFilters}
          disabledConfirm={isDisabledButtonFilter}
          disabledRefuse={isDisabledButtonClean}
          confirmText="Aplicar Filtros"
          refuseText="Limpar filtros"
          removePortalOnDestroy={false}
          heightBasedOnContent={false}
          height={windowHeight?.current}
          customContainerMaxHeight="90vh"
          customBodyMaxHeight="100%"
        >
          {renderFiltersContent()}
        </FuelBottomSheet>
      )}
    </>
  );
};

export default FiltersDrawer;
